import React, { memo } from "react";

import Avatar from "../Avatar";
import type { TypeDualAvatarProps } from "./DualAvatarTypes";

/** Matches the Avatar default so the secondary can always be derived from a size. */
const DEFAULT_SIZE = "40px";

/** The secondary avatar's size, as a fraction of the primary's. */
const SECONDARY_SCALE = 0.6;

/** How far the secondary overhangs the primary's corner, as a fraction of the size. */
const SECONDARY_OFFSET_SCALE = 0.1;

/**
 * Sizes are assumed to be px (as elsewhere in this package); anything else falls
 * back to a calc() so the value stays valid CSS instead of resolving to "NaNpx".
 */
const scaleSize = (
  size: string,
  scale: number,
  round: (value: number) => number = (value) => value
): string => {
  const numericSize = Number(size.replace("px", ""));

  return Number.isNaN(numericSize)
    ? `calc(${size} * ${scale})`
    : `${round(numericSize * scale)}px`;
};

/**
 * Two profile avatars shown as one unit, the secondary overlapping the primary's
 * bottom-right corner. Positioning lives in dual-avatar.css; the overhang is passed
 * to it through the --seeds-dual-avatar-offset custom property so it scales with
 * `size`, landing on a whole pixel so the two avatars stay crisply aligned.
 */
export const DualAvatar = React.forwardRef<HTMLDivElement, TypeDualAvatarProps>(
  (
    {
      primary,
      secondary,
      appearance,
      size = DEFAULT_SIZE,
      "aria-hidden": ariaHidden,
      className,
      style,
      ...rest
    }: TypeDualAvatarProps,
    ref
  ) => (
    <div
      {...rest}
      ref={ref}
      className={["seeds-dual-avatar", className].filter(Boolean).join(" ")}
      // Placed after the consumer's style so the offset can't be clobbered.
      style={
        {
          ...style,
          "--seeds-dual-avatar-offset": scaleSize(
            size,
            SECONDARY_OFFSET_SCALE,
            Math.ceil
          ),
        } as React.CSSProperties
      }
    >
      <Avatar
        {...primary}
        appearance={appearance}
        size={size}
        aria-hidden={ariaHidden}
      />
      <Avatar
        {...secondary}
        appearance={appearance}
        size={scaleSize(size, SECONDARY_SCALE)}
        aria-hidden={ariaHidden}
        className="seeds-dual-avatar-secondary"
      />
    </div>
  )
);

DualAvatar.displayName = "DualAvatar";

export default memo(DualAvatar);
